<template>
  <van-popup
    :show="modelValue"
    position="right"
    class="more-sheet-popup"
    overlay-class="more-sheet-overlay"
    :duration="restoreInstantly ? 0 : 0.3"
    @update:show="emit('update:modelValue', $event)"
  >
    <section class="more-sheet">
      <header class="more-sheet__header">
        <button
          class="more-sheet__close"
          type="button"
          :aria-label="t('common.close')"
          @click="emit('update:modelValue', false)"
        >
          <app-image :src="closeIcon"></app-image>
        </button>
        <h2>{{ t("wallet.moreFeatures") }}</h2>
      </header>

      <section class="more-sheet__section">
        <h3>{{ t("wallet.walletFeatures") }}</h3>
        <div class="more-sheet__grid">
          <button
            v-for="item in walletItems"
            :key="item.label"
            class="more-sheet__grid-list"
            type="button"
            @click="handleItem(item)"
          >
            <app-image :src="item.icon" alt="" />
            <span>{{ item.label }}</span>
          </button>
        </div>
      </section>

      <section class="more-sheet__section">
        <h3>{{ t("wallet.cardFeatures") }}</h3>
        <div class="more-sheet__grid">
          <button
            v-for="item in physicalItems"
            :key="item.label"
            class="more-sheet__grid-list"
            type="button"
            @click="handleItem(item)"
          >
            <app-image :src="item.icon" alt="" />
            <span>{{ item.label }}</span>
          </button>
        </div>
      </section>

      <section class="more-sheet__section">
        <h3>{{ t("wallet.personalFeatures") }}</h3>
        <div class="more-sheet__grid">
          <button
            v-for="item in personalItems"
            :key="item.label"
            class="more-sheet__grid-list"
            type="button"
            @click="handleItem(item)"
          >
            <div class="more-sheet__help-icon">
              <app-image :src="item.icon" alt="" />
            </div>
            <span>{{ item.label }}</span>
          </button>
        </div>
      </section>

      <section class="more-sheet__section">
        <h3>{{ t("wallet.helpAndSupport") }}</h3>
        <div class="more-sheet__grid">
          <button
            v-for="item in helpItems"
            :key="item.label"
            class="more-sheet__grid-list"
            type="button"
            @click="handleItem(item)"
          >
            <div class="more-sheet__help-icon">
              <app-image :src="item.icon" alt="" />
            </div>
            <span>{{ item.label }}</span>
          </button>
        </div>
      </section>

      <section class="more-sheet__section">
        <div class="more-sheet__section-title">
          <h3>{{ t("wallet.otherFeatures") }}</h3>
          <span>{{ t("wallet.fundTransferUnavailable") }}</span>
        </div>
        <div class="more-sheet__grid">
          <button
            v-for="item in otherItems"
            :key="item.label"
            :class="[
              'more-sheet__grid-list',
              { 'is-unavailable': item.unavailable },
            ]"
            type="button"
            @click="handleItem(item)"
          >
            <app-image :src="item.icon" alt="" />
            <span>{{ item.label }}</span>
          </button>
        </div>
      </section>
    </section>
  </van-popup>
</template>

<script setup>
import { computed } from "vue";
import { closeToast, showFailToast, showLoadingToast, showToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { pcCardCreatePre } from "@/api/card";
import { useWalletScanner } from "@/composables/useWalletScanner";
import { navigateWithState } from "@/utils/route-state";
import payeeIcon from "@/assets/images/homeImage/payee_icon.png";
import scanIcon from "@/assets/images/homeImage/scancode_icon.png";
import openCardIcon from "@/assets/images/homeImage/openCard_icon.png";
import activeCardIcon from "@/assets/images/homeImage/activeCard_icon.png";
import unbindCardIcon from "@/assets/images/homeImage/unbindCard_icon2.png";
import transferIcon from "@/assets/images/homeImage/tranfer_icon2.png";
import customIcon from "@/assets/images/SVG/icon/custom.svg";
import bookOpenIcon from "@/assets/images/SVG/icon/book-open.svg";
import kycIcon from "@/assets/images/SVG/icon/kyc.svg";
import safeIcon from "@/assets/images/SVG/icon/safeIcon.svg";
import closeIcon from "@/assets/images/close_no.png";
import { useProfileStore } from "@/stores/profile";

defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  restoreInstantly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "select", "preserve"]);
const { t } = useI18n();
const router = useRouter();
const { openWalletScanner } = useWalletScanner();
const profileStore = useProfileStore();

const walletItems = computed(() => [
  // { label: t('wallet.payment'), icon: payeeIcon, to: '/wallet/receive' },
  { label: t("wallet.scanCode"), icon: scanIcon, action: "scan" },
  { label: t("wallet.openCard"), icon: openCardIcon, action: "open-card" },
]);

const physicalItems = computed(() => [
  {
    label: t("wallet.activatePhysicalCardEntry"),
    icon: activeCardIcon,
    action: "activatePhysicalCard",
  },
  {
    label: t("card.unBindCardText_17"),
    icon: unbindCardIcon,
    to: "/card/unbind/list",
  },
]);

const otherItems = computed(() => [
  { label: t("wallet.fundTransfer"), icon: transferIcon, unavailable: true },
]);

const personalItems = computed(() => [
  { label: t("mine.kyc"), icon: kycIcon, to: "/card/kyc/result" },
  { label: t("mine.security"), icon: safeIcon, to: "/mine/security" },
]);

const helpItems = computed(() => [
  { label: t("wallet.contactUs"), icon: customIcon, to: "/mine/about" },
  {
    label: t("wallet.userGuide"),
    icon: bookOpenIcon,
    to: "/wallet/user-guide",
  },
]);

async function goActivatePhysicalCard() {
  if (!profileStore.platformAddress) {
    router.replace("/login");
    return;
  }

  showLoadingToast({
    message: t("common.loading"),
    forbidClick: true,
    duration: 0,
  });
  try {
    const result = await pcCardCreatePre({
      address: profileStore.platformAddress,
    });
    const holderStatus = Number(result?.holderStatus || 0);

    if (holderStatus === 3) {
      navigateWithState(router, "/wallet/physical-active", {
        namespace: "wallet-physical-active",
        payload: {
          email: result?.email || profileStore.currentAccount?.email || "",
        },
      });
      return;
    }

    if (
      holderStatus === 2 ||
      holderStatus === 4 ||
      (holderStatus === 1 && String(result?.reason || "").trim())
    ) {
      navigateWithState(router, "/card/kyc/result", {
        namespace: "card-kyc-result",
        payload: {
          holderStatus: String(holderStatus),
          reason: result?.reason || "",
          type: "1",
        },
      });
      return;
    }

    router.push("/card/kyc");
  } catch (error) {
    showFailToast(error.message || error.msg || t("common.retryLater"));
  } finally {
    closeToast();
  }
}

function handleItem(item) {
  if (item.unavailable) {
    showToast(t("wallet.fundTransferUnavailable"));
    return;
  }

  emit("preserve");
  emit("select", item);
  if (item.action === "scan") {
    openWalletScanner();
    return;
  }
  if (item.action === "activatePhysicalCard") {
    goActivatePhysicalCard();
    return;
  }
  if (item.to) {
    router.push(item.to);
    return;
  }
}
</script>

<style scoped lang="scss">
.more-sheet {
  box-sizing: border-box;
  width: 100%;
  min-height: 100%;
  padding: calc(16px + env(safe-area-inset-top)) 16px
    calc(24px + env(safe-area-inset-bottom));
  background: #ffffff;
}

.more-sheet__header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;

  h2 {
    margin: 0;
    color: var(--wp-color-text);
    font-size: 18px;
    font-weight: 700;
    text-align: center;
  }
}

.more-sheet__close {
  position: absolute;
  top: 50%;
  left: -4px;
  // display: grid;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  background: transparent;
  transform: translateY(-50%);

  .van-icon {
    color: var(--wp-color-sub-text);
    font-size: 18px;
  }

  .app-image {
    width: 18px;
    height: 18px;
    display: block;
  }
}

.more-sheet__section {
  margin-top: 24px;
}

.more-sheet__section-title {
  display: flex;
  align-items: baseline;
  gap: 6px;

  span {
    color: #ef0027;
    font-size: 11px;
    line-height: 1.4;
  }
}

h3 {
  margin: 0;
  color: var(--wp-color-text);
  font-size: 16px;
  font-weight: 700;
}

.more-sheet__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px 12px;
  margin-top: 16px;

  &-list {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--wp-color-text);
    font-size: 12px;
    line-height: 18px;
    white-space: pre-wrap;
    text-align: center;

    &.is-unavailable {
      color: var(--wp-color-text);
      cursor: default;
      opacity: 1;
    }
  }

  .app-image {
    width: 48px;
    height: 48px;
    margin: 0 auto 10px;
  }

  .more-sheet__help-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    margin: 0 auto 10px;
    border-radius: 50%;
    background: #f4f5f6;

    .app-image {
      width: 24px;
      height: 24px;
      margin: 0;
    }
  }
}

:global(.more-sheet-popup.van-popup--right) {
  width: 100vw;
  height: 100%;
  max-width: none;
}

:global(.more-sheet-overlay) {
  background: #ffffff !important;
}
</style>
